import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Block, CheckCircleOutline, Delete, Edit } from '@mui/icons-material';
import Title from '../../components/Title';
import AdminsDialog from './AdminsDialog';
import { getRoleIcon } from '../AdminRolesPage/roleIcons';
import {
  deleteAdminAsync,
  getAdminsAsync,
  selectAdmins,
  toggleAdminDisabledAsync
} from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { getTabsAsync } from '../../redux/slices/tabsSlice';
import MobileCardList, {
  MobileCardItem
} from '../../components/MobileCardList';
import useIsMobile from '../../hooks/useIsMobile';
import { Admin } from '../../resources/adminsTypes';
import { getAdminIdFromToken } from '../../resources/functions';

const PREFIX = 'AdminsPage';

const classes = {
  header: `${PREFIX}-header`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    marginBottom: 20
  }
});

const AdminsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const isMobile = useIsMobile();

  const actionButtonSx = useMemo(
    () => ({
      bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
      color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
      borderRadius: '8px',
      '&:hover': {
        bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A'
      }
    }),
    [theme.palette.mode]
  );

  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const citiesIds = useMemo(() => {
    if (isSuperAdmin) {
      return '';
    }

    const cities = (admins.info?.cities ?? []).map((item) => item?._id);
    const citiesIds = cities.join(',').toString()

    return citiesIds;
  }, [isSuperAdmin, admins.info]);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getAdminsAsync());
    }

    if (!isSuperAdmin && citiesIds) {
      dispatch(getAdminsAsync(citiesIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesIds]);

  useEffect(() => {
    if (!isSuperAdmin) {
      dispatch(getTabsAsync());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    adminId?: string;
  }>({ open: false });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<{ id: string; login: string } | null>(null);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (deleteConfirmOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [deleteConfirmOpen, countdown]);

  const openDialog = useCallback(
    (adminId?: string) => () => setDialogState({ open: true, adminId }),
    []
  );

  const closeDialog = useCallback(() => setDialogState({ open: false }), []);

  const openDeleteConfirm = useCallback(
    (adminId: string, adminLogin: string) => () => {
      setAdminToDelete({ id: adminId, login: adminLogin });
      setDeleteConfirmOpen(true);
      setCountdown(3);
    },
    []
  );

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setAdminToDelete(null);
    setCountdown(3);
  }, []);

  const reloadAdmins = useCallback(() => {
    if (isSuperAdmin) {
      dispatch(getAdminsAsync());
    } else if (citiesIds) {
      dispatch(getAdminsAsync(citiesIds));
    }
  }, [dispatch, isSuperAdmin, citiesIds]);

  const deleteAdmin = useCallback(async () => {
    if (!adminToDelete) return;

    await dispatch(deleteAdminAsync(adminToDelete.id));
    reloadAdmins();
    closeDeleteConfirm();
  }, [adminToDelete, dispatch, reloadAdmins, closeDeleteConfirm]);

  const currentAdminId = useMemo(
    () => admins.info?._id || getAdminIdFromToken(auth.info?.token),
    [admins.info, auth.info]
  );

  // Дзеркалить правила бекенда (controllers/Admin.js): себе заблокувати не можна,
  // суперадмін може будь-кого, решта - лише створених ними адмінів.
  const canToggleDisabled = useCallback(
    (admin: Admin) => {
      if (!currentAdminId || admin._id === currentAdminId) return false;
      if (isSuperAdmin) return true;
      return Boolean(admin.createdBy) && admin.createdBy === currentAdminId;
    },
    [isSuperAdmin, currentAdminId]
  );

  const toggleDisabled = useCallback(
    (admin: Admin) => async () => {
      try {
        await dispatch(toggleAdminDisabledAsync(admin._id, !admin.disabled));
        reloadAdmins();
      } catch (e: any) {
        alert(
          e?.response?.data?.message ||
            e?.message ||
            'Не вдалося змінити стан адміністратора'
        );
      }
    },
    [dispatch, reloadAdmins]
  );

  const renderActions = useCallback(
    (admin: Admin) => (
      <>
        {canToggleDisabled(admin) && (
          <Tooltip title={admin.disabled ? 'Розблокувати' : 'Заблокувати'}>
            <IconButton
              size='medium'
              onClick={toggleDisabled(admin)}
              sx={{
                bgcolor: admin.disabled
                  ? 'rgba(76, 175, 80, 0.12)'
                  : 'rgba(244, 67, 54, 0.12)',
                color: admin.disabled ? '#4CAF50' : '#F44336',
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: admin.disabled
                    ? 'rgba(76, 175, 80, 0.24)'
                    : 'rgba(244, 67, 54, 0.24)'
                }
              }}
            >
              {admin.disabled ? (
                <CheckCircleOutline fontSize='small' />
              ) : (
                <Block fontSize='small' />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title='Редагувати'>
          <IconButton
            size='medium'
            onClick={openDialog(admin._id)}
            sx={actionButtonSx}
          >
            <Edit fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Видалити'>
          <IconButton
            size='medium'
            onClick={openDeleteConfirm(admin._id, admin.login)}
            sx={actionButtonSx}
          >
            <Delete fontSize='small' />
          </IconButton>
        </Tooltip>
      </>
    ),
    [canToggleDisabled, toggleDisabled, openDialog, openDeleteConfirm, actionButtonSx]
  );

  const mobileItems = useMemo<MobileCardItem[]>(
    () =>
      admins.items.map(admin => ({
        id: admin._id,
        title: admin.login,
        badge: admin.disabled ? (
          <Chip label='Заблокований' size='small' color='error' variant='outlined' />
        ) : undefined,
        fields: [
          { label: 'Роль', value: admin.role?.name || 'Адміністратор' },
          {
            label: 'Міста',
            value: admin.cities?.map(city => city.cityName).join(', ') || '—',
            wide: true
          }
        ],
        actions: renderActions(admin)
      })),
    [admins.items, renderActions]
  );

  return (
    <Root>
      <Grid
        container
        alignItems='center'
        className={classes.header}
        spacing={4}
      >
        <Grid item>
          <Title>Адміністратори</Title>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            startIcon={<Add />}
            variant='contained'
            onClick={openDialog()}
          >
            Додати
          </Button>
        </Grid>
      </Grid>
      {isMobile ? (
        <MobileCardList items={mobileItems} emptyText='Адміністраторів не знайдено' />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Логін</TableCell>
                <TableCell align='right'>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.items.map(admin => {
                const AdminIcon = getRoleIcon(admin.role?.icon);
                return (
                  <TableRow key={admin._id}>
                    <TableCell sx={{ opacity: admin.disabled ? 0.5 : 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={admin.role?.name || 'Адміністратор'} arrow>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 213, 79, 0.12)'
                                  : 'rgba(66, 66, 66, 0.12)',
                              borderRadius: '8px'
                            }}
                          >
                            <AdminIcon
                              sx={{
                                fontSize: 24,
                                color:
                                  theme.palette.mode === 'dark'
                                    ? '#5073F8'
                                    : '#424242'
                              }}
                            />
                          </Avatar>
                        </Tooltip>
                        <span>{admin.login}</span>
                        {admin.disabled && (
                          <Chip
                            label='Заблокований'
                            size='small'
                            color='error'
                            variant='outlined'
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align='right'>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'flex-end'
                        }}
                      >
                        {renderActions(admin)}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <AdminsDialog {...dialogState} onClose={closeDialog} />
      
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити адміністратора "{adminToDelete?.login}"?
            {countdown > 0 && (
              <Box sx={{ mt: 2, color: 'warning.main', fontWeight: 500 }}>
                Кнопка стане активною через {countdown} сек...
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>
            Скасувати
          </Button>
          <Button 
            onClick={deleteAdmin} 
            color="error" 
            variant="contained"
            disabled={countdown > 0}
          >
            Видалити
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default AdminsPage;
